import React, { useEffect, useState } from 'react';
import { Form } from 'antd';

import { CustomInput } from '../../../components';
import type { SelectedProduct } from '../components/ProductMultiSelectField';
import { ProductSerialInput } from '../components/ProductQuantityRows';
import {
  BOX_REFERENCE_MAX_LENGTH,
  lineQty,
  referenceCount,
  type PackageLineValues,
} from './lines';
import styles from './styles.module.css';

/** What the serial inputs need from the products slot. */
export interface SerialSlot {
  getProductName: (id: string) => string;
  /** Duplicate-serial message for one serial input, or undefined. `box` is the index within the line. */
  serialWarning: (
    lineIndex: number,
    box: number,
    productId: string
  ) => string | undefined;
}

export interface BoxScopeProps {
  lineIndex: number;
  slot?: SerialSlot;
  /** The line's real products (never `all_products`). */
  lineProducts: SelectedProduct[];
}

/** One serial input per product for a single box. Names are relative to `<Form.List name='lines'>`. */
export const SerialInputs: React.FC<BoxScopeProps & { box: number }> = ({
  lineIndex,
  box,
  slot,
  lineProducts,
}) => (
  <>
    {slot &&
      lineProducts.map((p) => {
        const name = slot.getProductName(p.id);
        const warning = slot.serialWarning(lineIndex, box, p.id);
        return (
          <div key={p.id} className={styles.serialRow}>
            <div className='shipment-product-row__name'>{name}</div>
            <Form.Item
              noStyle
              name={[lineIndex, 'boxes', box, 'serials', p.id]}
            >
              <ProductSerialInput
                ariaLabel={`Serial number for ${name} in box ${box + 1}`}
              />
            </Form.Item>
            {warning && (
              <div role='alert' className='shipment-product-row__warning'>
                {warning}
              </div>
            )}
          </div>
        );
      })}
  </>
);

/** Collapsible per-box references (and serials) for a line with more than one box. */
export const BoxDetails: React.FC<
  BoxScopeProps & { line: PackageLineValues }
> = ({ line, ...scope }) => {
  const qty = lineQty(line);
  const hasProducts = scope.lineProducts.length > 0;
  const [open, setOpen] = useState(hasProducts);
  // The serial inputs live here, so the first product opens the block.
  useEffect(() => {
    if (hasProducts) setOpen(true);
  }, [hasProducts]);
  const withRef = referenceCount(line);
  const verb = open ? 'Hide' : withRef > 0 ? 'Edit' : 'Add';

  return (
    <div className={styles.boxDetails}>
      <div className={styles.boxDetailsHeader}>
        <div>
          <div className={styles.lineCardTitle}>
            {hasProducts
              ? 'Box references and serial numbers'
              : 'Box references'}
          </div>
          <div className={styles.boxDetailsStatus}>
            {withRef > 0
              ? `${withRef} of ${qty} boxes have a reference`
              : 'Optional, one per box'}
          </div>
        </div>
        <button
          type='button'
          className={styles.lineAction}
          aria-expanded={open}
          aria-label={`${verb} box references`}
          onClick={() => setOpen((o) => !o)}
        >
          {verb}
        </button>
      </div>
      {open &&
        Array.from({ length: qty }, (_, b) => (
          <div key={b} className={styles.boxRow}>
            <div className={styles.boxRowLabel}>Box {b + 1}</div>
            <div className={styles.boxRowFields}>
              <Form.Item
                noStyle
                name={[scope.lineIndex, 'boxes', b, 'reference']}
              >
                <CustomInput
                  aria-label={`Reference for box ${b + 1}`}
                  placeholder='Optional'
                  height='36px'
                  maxLength={BOX_REFERENCE_MAX_LENGTH}
                />
              </Form.Item>
              <SerialInputs {...scope} box={b} />
            </div>
          </div>
        ))}
    </div>
  );
};
